import { ChangeEvent, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { InputField } from "@/components/InputField/InputField";

interface WebhookUrlFieldProps {
	/** The URL as the API sent it: masked where it holds a secret. */
	shownUrl: string;
	urlMasked?: boolean;
	isReplacing: boolean;
	/** The replacement typed so far; read only while replacing. */
	value: string;
	error?: boolean;
	onReplace: () => void;
	onKeep: () => void;
	onChange: (value: string) => void;
}

const LINK_BUTTON = "text-brand-light hover:underline cursor-pointer";

/**
 * The Edit dialog's URL (PREC-4253). The browser never holds the stored URL, only its masked form,
 * so that form is shown read-only: "Replace URL" opens an empty field for a new one, and a save
 * that does not replace it keeps the stored URL (`buildWebhookUpdate` leaves `url` out).
 */
export const WebhookUrlField: FunctionComponent<WebhookUrlFieldProps> = ({
	shownUrl,
	urlMasked,
	isReplacing,
	value,
	error,
	onReplace,
	onKeep,
	onChange,
}) => {
	const { t } = useTranslation();

	if (!isReplacing) {
		return (
			<div className="flex flex-col gap-1 mb-1">
				<InputField
					isDisabled
					className="font-mono"
					label={t("webhookLists.url.label")}
					name="url"
					testId="webhook-url-shown"
					value={shownUrl}
				/>
				<span className="text-xs text-text-meta">
					{urlMasked ? `${t("webhookLists.url.maskedHint")} ` : ""}
					<button
						className={LINK_BUTTON}
						data-test="webhook-url-replace"
						type="button"
						onClick={onReplace}
					>
						{t("webhookLists.url.replace")}
					</button>{" "}
					{t("webhookLists.url.replaceHintAfter")}
				</span>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-1 mb-1">
			<InputField
				autoFocus
				className="font-mono"
				error={error}
				handleChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
				label={t("webhookLists.url.label")}
				name="url"
				placeholder="e.g. https://example.com/webhook"
				testId="webhook-url-input"
				value={value}
			/>
			<span className="text-xs text-text-meta">
				{t("webhookLists.url.replaceHint")}{" "}
				<button className={LINK_BUTTON} data-test="webhook-url-keep" type="button" onClick={onKeep}>
					{t("webhookLists.url.keepCurrent")}
				</button>
			</span>
		</div>
	);
};
